// Field analytics — per-network applications & signups per day (last 30 days).
import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { adminAnalytics } from "@/lib/api";

const SERIES = [
  { key: "student", label: "Students", color: "#E7C873" },
  { key: "interest", label: "Talent/Mentors", color: "#F4EFE6" },
  { key: "school", label: "Schools", color: "#7FB4D9" },
  { key: "brand", label: "Brands", color: "#3ad07a" },
  { key: "signups", label: "Signups", color: "#8E939C" },
];

const fmtTick = (d) => `${parseInt(d.slice(5, 7), 10)}/${parseInt(d.slice(8, 10), 10)}`;

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-gold/40 bg-onyx px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em]">
      <p className="text-silver/60">{label}</p>
      {payload.map((p) => {
        const s = SERIES.find((x) => x.key === p.dataKey);
        return <p key={p.dataKey} style={{ color: s?.color }}>{s?.label || p.dataKey}: {p.value}</p>;
      })}
    </div>
  );
};

export const FieldAnalytics = () => {
  const [data, setData] = useState(null);

  useEffect(() => { adminAnalytics().then(setData).catch(() => setData({ error: true })); }, []);

  if (!data) return <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50">Loading field analytics...</p>;
  if (data.error) return <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50">Analytics unavailable.</p>;

  return (
    <div className="border border-silver/20 bg-charcoal/60 p-6" data-testid="field-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Field analytics · last 30 days</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[9px] uppercase tracking-[0.15em]">
          {SERIES.map((s) => (
            <span key={s.key} style={{ color: s.color }} data-testid={`analytics-total-${s.key}`}>■ {s.label}: {data.totals[s.key] ?? 0}</span>
          ))}
        </div>
      </div>
      <div className="mt-4 h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.days} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`fill-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="rgba(201,162,39,0.08)" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtTick} interval={4} tick={{ fill: "#6b6f76", fontSize: 9, fontFamily: "monospace" }} axisLine={{ stroke: "rgba(201,162,39,0.2)" }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: "#6b6f76", fontSize: 9, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} />
            {SERIES.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={s.key === "student" ? 2 : 1.5}
                strokeDasharray={s.key === "signups" ? "4 3" : undefined} fill={`url(#fill-${s.key})`} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
